/**
 * useCommission372 — Issue #372 hooks for the multi-stage commission
 * release / freeze / clawback / payout candidate / template export flows.
 *
 * Typed against the generated OpenAPI schema; no hand-rolled fetch / axios,
 * no fake state, no Decimal -> number coercion on the frontend.
 */
import { useCallback, useState } from 'react';
import { ApiError as ApiClientError, createApiClient } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type CommissionRule = ApiComponents['schemas']['CommissionRuleResponseDto'];
export type CommissionDetail =
  ApiComponents['schemas']['CommissionDetailResponseDto'];
export type CommissionEvent =
  ApiComponents['schemas']['CommissionEventResponseDto'];
export type CommissionPayoutCandidate =
  ApiComponents['schemas']['CommissionPayoutCandidateResponseDto'];
export type CommissionWideSheet =
  ApiComponents['schemas']['CommissionWideSheetResponseDto'];

export type CreateCommissionRule =
  ApiComponents['schemas']['CreateCommissionRuleDto'];
export type UpdateCommissionRuleStatus =
  ApiComponents['schemas']['UpdateCommissionRuleStatusDto'];
export type CreateCommissionDetail =
  ApiComponents['schemas']['CreateCommissionDetailDto'];
export type FreezeCommissionDetail =
  ApiComponents['schemas']['FreezeCommissionDetailDto'];
export type UnfreezeCommissionDetail =
  ApiComponents['schemas']['UnfreezeCommissionDetailDto'];
export type ClawbackCommissionDetail =
  ApiComponents['schemas']['ClawbackCommissionDetailDto'];
export type ReverseCommissionDetail =
  ApiComponents['schemas']['ReverseCommissionDetailDto'];
export type BuildCommissionPayoutCandidate =
  ApiComponents['schemas']['BuildCommissionPayoutCandidateDto'];
export type ConfirmCommissionPayoutCandidate =
  ApiComponents['schemas']['ConfirmCommissionPayoutCandidateDto'];
export type CancelCommissionPayoutCandidate =
  ApiComponents['schemas']['CancelCommissionPayoutCandidateDto'];
export type LinkCommissionPayoutCandidate =
  ApiComponents['schemas']['LinkCommissionPayoutCandidateDto'];
export type RenderCommissionWideSheet =
  ApiComponents['schemas']['RenderCommissionWideSheetDto'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, httpStatus?: number): ApiError {
  const status =
    httpStatus ?? (err instanceof ApiClientError ? err.status : undefined);
  if (status === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (status === 403) {
    return { message: '当前账号无权执行该提成操作', code: 'FORBIDDEN' };
  }
  if (status === 409) {
    return { message: '提成数据已被更新，请刷新后重试', code: 'CONFLICT' };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

// ─── Rules ───────────────────────────────────────────────────────────────────

export function useCommissionRules() {
  const [rules, setRules] = useState<CommissionRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchRules = useCallback(
    async (params?: {
      ruleCode?: string;
      projectType?: 'default' | 'self_operated_core' | 'cooperative' | 'channel';
      roleType?:
        | 'development'
        | 'follow_up'
        | 'lead_close'
        | 'assistant_close'
        | 'base_service'
        | 'solution_writing';
      status?: 'draft' | 'active' | 'inactive' | 'archived';
      effectiveAt?: string;
      limit?: number;
      offset?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/finance/commission/rules',
          { params: { query: params } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setRules([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createRule = useCallback(
    async (dto: CreateCommissionRule): Promise<CommissionRule | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/commission/rules',
          { body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const updateRuleStatus = useCallback(
    async (
      id: string,
      dto: UpdateCommissionRuleStatus,
    ): Promise<CommissionRule | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/commission/rules/{id}/status',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { rules, loading, error, fetchRules, createRule, updateRuleStatus };
}

// ─── Details ─────────────────────────────────────────────────────────────────

export function useCommissionDetails() {
  const [details, setDetails] = useState<CommissionDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchDetails = useCallback(
    async (params?: {
      orderProjectId?: string;
      employeeId?: string;
      roleType?:
        | 'development'
        | 'follow_up'
        | 'lead_close'
        | 'assistant_close'
        | 'base_service'
        | 'solution_writing';
      releaseStatus?:
        | 'pending_unlock'
        | 'released'
        | 'frozen'
        | 'clawback_pending'
        | 'clawed_back'
        | 'reversed';
      payoutStatus?:
        | 'not_eligible'
        | 'payout_ready'
        | 'payout_linked'
        | 'paid'
        | 'payout_blocked';
      payoutCandidateId?: string;
      limit?: number;
      offset?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/finance/commission/details',
          { params: { query: params } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setDetails([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createDetail = useCallback(
    async (dto: CreateCommissionDetail): Promise<CommissionDetail | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/commission/details',
          { body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const freezeDetail = useCallback(
    async (
      id: string,
      dto: FreezeCommissionDetail,
    ): Promise<CommissionDetail | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/commission/details/{id}/freeze',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const unfreezeDetail = useCallback(
    async (
      id: string,
      dto: UnfreezeCommissionDetail,
    ): Promise<CommissionDetail | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/commission/details/{id}/unfreeze',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Step 1: released | frozen -> clawback_pending.
  // Backend `requestClawback` is the only path the state machine accepts
  // from non-terminal `released` / `frozen`; calling `executeClawback`
  // directly will be rejected with 409.
  const requestClawback = useCallback(
    async (
      id: string,
      dto: ClawbackCommissionDetail,
    ): Promise<CommissionDetail | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/commission/details/{id}/clawback-request',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Step 2: clawback_pending -> clawed_back.
  const executeClawback = useCallback(
    async (
      id: string,
      dto: ClawbackCommissionDetail,
    ): Promise<CommissionDetail | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/commission/details/{id}/clawback',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reverseDetail = useCallback(
    async (
      id: string,
      dto: ReverseCommissionDetail,
    ): Promise<CommissionDetail | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/commission/details/{id}/reverse',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    details,
    loading,
    error,
    fetchDetails,
    createDetail,
    freezeDetail,
    unfreezeDetail,
    requestClawback,
    executeClawback,
    reverseDetail,
  };
}

// ─── Events ──────────────────────────────────────────────────────────────────

export function useCommissionEvents() {
  const [events, setEvents] = useState<CommissionEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchEvents = useCallback(async (detailId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/finance/commission/details/{id}/events',
        { params: { path: { id: detailId } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setEvents([...(data ?? [])]);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { events, loading, error, fetchEvents };
}

// ─── Payout candidates ───────────────────────────────────────────────────────

export function useCommissionPayoutCandidates() {
  const [candidates, setCandidates] = useState<CommissionPayoutCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchCandidates = useCallback(
    async (params?: {
      status?: 'draft' | 'confirmed' | 'linked' | 'cancelled';
      periodYear?: number;
      periodMonth?: number;
      limit?: number;
      offset?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/finance/commission/payout-candidates',
          { params: { query: params } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setCandidates([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const buildCandidate = useCallback(
    async (
      dto: BuildCommissionPayoutCandidate,
    ): Promise<CommissionPayoutCandidate | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/commission/payout-candidates',
          { body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const confirmCandidate = useCallback(
    async (
      id: string,
      dto: ConfirmCommissionPayoutCandidate,
    ): Promise<CommissionPayoutCandidate | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/commission/payout-candidates/{id}/confirm',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cancelCandidate = useCallback(
    async (
      id: string,
      dto: CancelCommissionPayoutCandidate,
    ): Promise<CommissionPayoutCandidate | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/commission/payout-candidates/{id}/cancel',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    candidates,
    loading,
    error,
    fetchCandidates,
    buildCandidate,
    confirmCandidate,
    cancelCandidate,
  };
}

// ─── Wide sheet export ───────────────────────────────────────────────────────

export function useCommissionWideSheet() {
  const [sheet, setSheet] = useState<CommissionWideSheet | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const render = useCallback(async (dto: RenderCommissionWideSheet) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/commission/export/wide-sheet',
        { body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      setSheet(data ?? null);
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { sheet, loading, error, render };
}
